import React, { useEffect, useState } from 'react'
import 'react-multi-carousel/lib/styles.css'
import 'semantic-ui-css/semantic.min.css'
import './library.css'
import styles from './styles'
import { ascendingSort } from '../../utils/sort'
import {
  Button,
  CircularProgress,
  Drawer,
  Grid,
  Stack,
  useTheme
} from '@mui/material'
import DropDownFilter from '../filter/DropDownFilter'
import DateRange from '../date/DateRange'
import { DARK, LIGHT, routePaths } from '../../constants'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../redux/reducers'
import {
  deleteSavedArticles,
  getIssues,
  getMagazines,
  getSavedArticles
} from '../../services/client'
import { LibraryIssue } from '../../../../../common/interfaces/Issue'
import { logout, updateUUID } from '../../redux/actions/userActions'
import AllIssues from '../issues/AllIssues'
import OpenIssues from '../issues/OpenIssues'
import { updateTheme } from '../../redux/actions/settingsActions'
import BackgroundMode from '../toggle/BackgroundMode'
import { KEY_THEME, saveItem } from '../../services/storage'
import { useClientApiContext } from '../context/ClientApiContextProvider'
import { SavedArticle } from '../../type/article'
import DrawerContent from '../drawer/DrawerContent'
import DrawerDetails from '../drawer/DrawerDetails'
import { EntitlementArticle } from '../../../../../common/interfaces/EntitlementArticle'
import SkeletonIssueCard from '../skeleton/SkeletonIssueCard'

const Library = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [issuesLoading, setIssuesLoading] = useState(true)
  const { user, settings } = useSelector((state: RootState) => state)
  const [open, setOpen] = useState(false)
  const [showSavedArticleDetails, setShowSavedArticleDetails] = useState(false)
  const [issues, setIssues] = useState<LibraryIssue[]>([])
  const [savedArticles, setSavedArticles] = useState<SavedArticle[]>([])
  const [savedArticleDetails, setSavedArticleDetails] = useState<SavedArticle>()
  const [filterTitle, setFilterTitle] = useState<string | undefined>()
  const [startDate, setStartDate] = useState<Date | null | undefined>()
  const [endDate, setEndDate] = useState<Date | null | undefined>()
  const [currentOpenIssue, setCurrentOpenIssue] = useState<LibraryIssue>()

  const { palette } = useTheme()
  const imageClass = `${palette.mode}-library-logo`
  const useClientContext = useClientApiContext()

  const { theme } = settings

  const handleLogout = () => {
    dispatch(logout())
    navigate(routePaths.HOME)
  }

  const updateIssues = (showLoadingIndicator: boolean = true) => {
    if (user.auth.accessToken) {
      getLatestIssue('', showLoadingIndicator)
    }
  }

  useEffect(() => {
    const onFocus = () => {
      updateIssues(false)
    }
    window.addEventListener('focus', onFocus)
    return () => {
      window.removeEventListener('focus', onFocus)
    }
  })

  useEffect(() => {
    if (showSavedArticleDetails && showSavedArticleDetails) {
      setSavedArticleDetails(
        savedArticles.filter(
          (result) =>
            result.entitlementId === savedArticleDetails?.entitlementId
        )[0]
      )
    }
    // eslint-disable-next-line
  }, [savedArticles])

  useEffect(() => {
    if (!user.id) {
      // Create uuid and store it if it is not existed
      dispatch(updateUUID((Math.random() * 100).toString(36).slice(3)))
    }
  }, [dispatch, user.id])

  useEffect(() => {
    if (!user.auth.accessToken) {
      dispatch(logout())
      navigate(routePaths.HOME)
    }
  }, [dispatch, navigate, user.auth.accessToken])

  const deleteArticles = async (
    articles: EntitlementArticle[],
    callback: () => void
  ) => {
    deleteSavedArticles(useClientContext.client, articles).then((result) => {
      if (result) {
        getLatestIssue('', false)
      }

      callback()
    })
  }

  const getLatestIssue = (
    id?: string,
    showLoadingIndicator: boolean = true
  ) => {
    showLoadingIndicator && setIssuesLoading(true)
    getIssues(useClientContext.client).then(
      (results) => {
        setIssues(results)
        setIssuesLoading(false)
        getArticles(results, showLoadingIndicator)
        if (id) {
          const currentIssue = results.filter(
            (issue) => issue.entitlementId === id
          )
          setCurrentOpenIssue(currentIssue[0])
        }
        getOrgIds(results)
      },
      (err) => {
        console.warn('getIssues error are: ', err)
        setIssuesLoading(false)
      }
    )
  }

  const getArticles = (
    issues: LibraryIssue[],
    showLoadingIndicator: boolean
  ) => {
    showLoadingIndicator && setLoading(true)
    getSavedArticles(useClientContext.client, issues).then(
      (results) => {
        setSavedArticles(results)
        showLoadingIndicator && setLoading(false)
      },
      (err) => {
        console.warn('getSavedArticles error are: ', err)
        showLoadingIndicator && setLoading(false)
      }
    )
  }

  const getOrgIds = (issues: LibraryIssue[]) => {
    getMagazines(
      useClientContext.client,
      issues.map((issue: LibraryIssue) => issue.umc).join('|')
    ).catch((err) => {
      console.warn('getMagazines error: ', err)
    })
  }

  useEffect(() => {
    updateIssues()
    // eslint-disable-next-line
  }, [user.auth.accessToken])

  const onTitleFilterChange = (title: string) => {
    setFilterTitle(title)
  }

  const renderLogoutButton = () => {
    return (
      <Button
        variant="contained"
        style={styles.logoutBtn}
        onClick={handleLogout}
      >
        Log out
      </Button>
    )
  }

  const changeTheme = () => {
    const mode = theme === LIGHT ? DARK : LIGHT
    saveItem(KEY_THEME, mode)
    dispatch(updateTheme(mode))
  }

  const openIssue = async (
    id: string = '',
    updateOpenIssue: boolean = true,
    articleId: string | null = null
  ) => {
    const currentIssue = issues.filter((issue) => issue.entitlementId === id)
    updateOpenIssue && setCurrentOpenIssue(currentIssue[0])
    window.open(
      articleId
        ? `/magazine?id=${id}&article=${articleId}`
        : `/magazine?id=${id}`
    )

    setTimeout(() => {
      getLatestIssue(id, false)
    }, 1000)
  }

  const openSavedArticle = async () => {
    setOpen(true)
  }

  const onDateChange = (startDate: Date | null, endDate: Date | null) => {
    setStartDate(startDate)
    setEndDate(endDate)
  }

  // People app ad specific to DDM titles
  // const peopleAdImg = () => {
  //   const data = {
  //     toWarehouse: true,
  //     src:
  //       window.innerWidth > 740
  //         ? './images/ads/people_app_ad_550x75.png'
  //         : './images/ads/people_app_ad_200x25.png',
  //     width: window.innerWidth > 740 ? 550 : 200,
  //     height: window.innerWidth > 740 ? 75 : 25,
  //     idmUser: { clientId: user.id, email: user.email },
  //     settings,
  //     userAgent: navigator.userAgent
  //   }
  //   return (
  //     <a
  //       href="https://apps.apple.com/us/app/people-entertainment-news/id6740392789"
  //       rel="noreferrer"
  //       target="_blank"
  //       onClick={() => {
  //         postLog(useClientContext.client, {
  //           level: 'info',
  //           message: 'ad_click',
  //           data
  //         })
  //       }}
  //     >
  //       <img
  //         src={
  //           window.innerWidth > 740
  //             ? './images/ads/people_app_ad_550x75.png'
  //             : './images/ads/people_app_ad_200x25.png'
  //         }
  //         alt="people_app_ad"
  //         style={{ width: '100%', height: '100%' }}
  //         onLoad={() => {
  //           postLog(useClientContext.client, {
  //             level: 'info',
  //             message: 'ad_load',
  //             data
  //           })
  //         }}
  //       />
  //     </a>
  //   )
  // }

  const renderContent = () => {
    return (
      <Grid item style={styles.container}>
        <header
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 20
          }}
        >
          <div className={imageClass} />
          {/* {orgIds.includes('352c9011-6869-41ee-ade8-188b30a31e22') && // desktop: on DDM title in library, show People app ad
            window.innerWidth > 740 && (
              <div className={adContainerDesktop}>{peopleAdImg()}</div>
            )} */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            <BackgroundMode theme={theme} onClick={changeTheme} />
            {renderLogoutButton()}
          </div>
        </header>
        {/* {orgIds.includes('352c9011-6869-41ee-ade8-188b30a31e22') && // mobile: on DDM title in library, show People app ad
          window.innerWidth <= 740 && (
            <div className={adContainerMobile}>{peopleAdImg()}</div>
          )} */}
        <div
          className="filter-container"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap'
          }}
        >
          <div
            style={{
              display: 'flex',
              gap: 20,
              alignItems: 'center',
              flexWrap: 'wrap'
            }}
          >
            <DropDownFilter
              data={Array.from(
                new Set(
                  ascendingSort(issues, 'magTitle').map(
                    (issue: LibraryIssue) => issue.magTitle
                  )
                )
              )}
              onTitleFilterChange={onTitleFilterChange}
              label={'Title'}
            />
            <DateRange onDateChange={onDateChange} />
          </div>
          <Button
            variant="outlined"
            style={{
              ...styles.savedArticlesBtn,
              fontSize: '12px',
              padding: '6px 12px',
              minWidth: 'auto'
            }}
            onClick={openSavedArticle}
          >
            Saved Articles ({savedArticles.length})
          </Button>
        </div>
        {issuesLoading ? (
          <>
            <div style={{ marginBottom: 40 }}>
              <div style={{ fontSize: 24, fontWeight: 600, marginBottom: 20 }}>
                Latest Issues
              </div>
              <SkeletonIssueCard variant="large" count={4} />
            </div>
            <div style={{ marginBottom: 40 }}>
              <div style={{ fontSize: 24, fontWeight: 600, marginBottom: 20 }}>
                Continue Reading
              </div>
              <SkeletonIssueCard variant="small" count={5} />
            </div>
          </>
        ) : (
          <>
            <AllIssues
              issues={issues}
              title={filterTitle}
              startDate={startDate}
              endDate={endDate}
              onClick={openIssue}
            />
            <OpenIssues
              issues={issues}
              title={filterTitle}
              startDate={startDate}
              endDate={endDate}
              onClick={openIssue}
              currentOpenIssue={currentOpenIssue}
            />
            {loading && (
              <Stack alignItems="center" sx={{ marginTop: 2 }}>
                <CircularProgress size={30} />
              </Stack>
            )}
          </>
        )}
      </Grid>
    )
  }

  const onSavedMagazineItemClicked = (article: SavedArticle) => {
    setSavedArticleDetails(article)
    setShowSavedArticleDetails(true)
  }

  const toggleDrawer = () => {
    setOpen(!open)
  }

  useEffect(() => {
    if (!open) {
      setSavedArticleDetails(undefined)
      setShowSavedArticleDetails(false)
    }
  }, [open])

  return (
    <>
      {renderContent()}
      <Drawer
        disableScrollLock={false}
        disableRestoreFocus={false}
        anchor={'left'}
        PaperProps={{
          sx: {
            width: 275,
            '&:hover': {
              overflowY: 'auto'
            },
            '&::-webkit-scrollbar': {
              display: 'none'
            }
          }
        }}
        open={open}
        onClose={toggleDrawer}
      >
        {showSavedArticleDetails && savedArticleDetails ? (
          <DrawerDetails
            onClick={setShowSavedArticleDetails}
            openIssue={openIssue}
            article={savedArticleDetails}
            deleteArticles={deleteArticles}
          />
        ) : (
          <DrawerContent
            onClick={setOpen}
            articles={savedArticles}
            onListItemClick={onSavedMagazineItemClicked}
            deleteArticles={deleteArticles}
          />
        )}
      </Drawer>
    </>
  )
}

export default Library
